function escapePdfText(text) {
    if (!text)
        return '';
    return text
        .replace(/\\/g, '\\\\')
        .replace(/\(/g, '\\(')
        .replace(/\)/g, '\\)');
}
/**
 * Generates an official, publication-quality PDF summary of the Transportation Request
 * Returns the raw binary Buffer/Uint8Array conforming to PDF-1.4 standard
 */
export function generateOrderSummaryPdf(data) {
    var width = 595.28; // Standard A4 width in points
    var height = 841.89; // Standard A4 height in points
    var ops = [];
    var fillRect = function (x, y, w, h, r, g, b) {
        ops.push("".concat(r.toFixed(3), " ").concat(g.toFixed(3), " ").concat(b.toFixed(3), " rg"));
        ops.push("".concat(x.toFixed(2), " ").concat(y.toFixed(2), " ").concat(w.toFixed(2), " ").concat(h.toFixed(2), " re f"));
    };
    var strokeRect = function (x, y, w, h, r, g, b, lineWidth) {
        if (lineWidth === void 0) { lineWidth = 1; }
        ops.push("".concat(lineWidth, " w"));
        ops.push("".concat(r.toFixed(3), " ").concat(g.toFixed(3), " ").concat(b.toFixed(3), " RG"));
        ops.push("".concat(x.toFixed(2), " ").concat(y.toFixed(2), " ").concat(w.toFixed(2), " ").concat(h.toFixed(2), " re s"));
    };
    var drawLine = function (x1, y1, x2, y2, r, g, b, lineWidth) {
        if (lineWidth === void 0) { lineWidth = 1; }
        ops.push("".concat(lineWidth, " w"));
        ops.push("".concat(r.toFixed(3), " ").concat(g.toFixed(3), " ").concat(b.toFixed(3), " RG"));
        ops.push("".concat(x1.toFixed(2), " ").concat(y1.toFixed(2), " m ").concat(x2.toFixed(2), " ").concat(y2.toFixed(2), " l s"));
    };
    var drawText = function (text, x, y, options) {
        if (options === void 0) { options = {}; }
        var font = options.font === 'bold' ? '/F2' : '/F1';
        var size = options.size || 10;
        var r = options.r !== undefined ? options.r : 0.17;
        var g = options.g !== undefined ? options.g : 0.17;
        var b = options.b !== undefined ? options.b : 0.17;
        ops.push('BT');
        ops.push("".concat(font, " ").concat(size, " Tf"));
        ops.push("".concat(r.toFixed(3), " ").concat(g.toFixed(3), " ").concat(b.toFixed(3), " rg"));
        ops.push("".concat(x.toFixed(2), " ").concat(y.toFixed(2), " Td"));
        ops.push("(".concat(escapePdfText(String(text || '')), ") Tj"));
        ops.push('ET');
    };
    // 1. Clean Canvas Background
    fillRect(0, 0, width, height, 0.985, 0.985, 0.99);
    // 2. Header Banner
    // Speedyssey brand orange accent bar
    fillRect(0, height - 6, width, 6, 0.961, 0.318, 0.035); // #F55109
    // Sleek dark header
    fillRect(0, height - 94, width, 88, 0.082, 0.082, 0.082); // #151515
    // Header Title & Branding
    drawText('SPEEDYSSEY LOGISTICS', 40, height - 36, { font: 'bold', size: 20, r: 1, g: 1, b: 1 });
    drawText('TRANSPORTATION & FREIGHT SERVICES', 40, height - 50, { font: 'bold', size: 9, r: 0.961, g: 0.318, b: 0.035 });
    drawText('ORDER REQUEST SUMMARY', 40, height - 72, { font: 'bold', size: 13, r: 0.9, g: 0.9, b: 0.9 });
    // Right side of Header: Reference ID Box
    var refBoxX = width - 215;
    var refBoxY = height - 80;
    fillRect(refBoxX, refBoxY, 175, 52, 0.16, 0.16, 0.16);
    strokeRect(refBoxX, refBoxY, 175, 52, 0.961, 0.318, 0.035, 1.2);
    drawText('REQUEST REFERENCE ID', refBoxX + 12, refBoxY + 34, { font: 'bold', size: 8, r: 0.7, g: 0.7, b: 0.7 });
    drawText(data.referenceNumber || 'SPD-TR-REQUEST', refBoxX + 12, refBoxY + 16, { font: 'bold', size: 12.5, r: 1, g: 0.5, b: 0.2 });
    // 3. Status Bar
    var statusY = height - 128;
    fillRect(40, statusY, width - 80, 26, 0.94, 0.97, 0.95);
    strokeRect(40, statusY, width - 80, 26, 0.78, 0.90, 0.82, 1);
    drawText('STATUS:', 54, statusY + 8, { font: 'bold', size: 9, r: 0.08, g: 0.52, b: 0.24 });
    drawText('REQUEST SUBMITTED - PENDING DISPATCH REVIEW', 106, statusY + 8, { font: 'bold', size: 9, r: 0.08, g: 0.40, b: 0.18 });
    var dateStr = data.submittedAt || new Date().toLocaleString();
    drawText("Submitted: ".concat(dateStr), width - 240, statusY + 8, { font: 'normal', size: 8.5, r: 0.35, g: 0.45, b: 0.4 });
    // Helper card renderer
    var drawCard = function (x, y, w, h, title) {
        fillRect(x, y, w, h, 1, 1, 1);
        strokeRect(x, y, w, h, 0.88, 0.88, 0.90, 1);
        fillRect(x, y + h - 28, w, 28, 0.96, 0.96, 0.97);
        drawLine(x, y + h - 28, x + w, y + h - 28, 0.88, 0.88, 0.90, 1);
        drawText(title.toUpperCase(), x + 16, y + h - 19, { font: 'bold', size: 9.5, r: 0.15, g: 0.15, b: 0.15 });
    };
    // Section 1: Customer Information Card
    var custY = height - 230;
    drawCard(40, custY, width - 80, 92, '1. Customer Information');
    drawText('Full Name:', 56, custY + 46, { font: 'bold', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.fullName || '—', 56, custY + 30, { font: 'bold', size: 10.5, r: 0.1, g: 0.1, b: 0.1 });
    drawText('Company Name:', 180, custY + 46, { font: 'bold', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.companyName || '—', 180, custY + 30, { font: 'normal', size: 10, r: 0.2, g: 0.2, b: 0.2 });
    drawText('Phone Number:', 320, custY + 46, { font: 'bold', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.phoneNumber || '—', 320, custY + 30, { font: 'normal', size: 10, r: 0.2, g: 0.2, b: 0.2 });
    drawText('Email Address:', 440, custY + 46, { font: 'bold', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.email || '—', 440, custY + 30, { font: 'normal', size: 9.5, r: 0.2, g: 0.2, b: 0.2 });
    // Section 2 & 3: Route Details (Pickup Origin & Delivery Destination)
    var colW = (width - 80 - 14) / 2;
    var routeY = height - 370;
    var routeH = 130;
    // Pickup Origin
    drawCard(40, routeY, colW, routeH, '2. Pickup Origin');
    drawText('Pickup Address:', 56, routeY + routeH - 45, { font: 'bold', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    var pickupAddr = data.pickupAddress || '—';
    var pickupAddrLines = pickupAddr.length > 42 ? [pickupAddr.substring(0, 42), pickupAddr.substring(42, 84)] : [pickupAddr];
    drawText(pickupAddrLines[0], 56, routeY + routeH - 60, { font: 'bold', size: 9.5, r: 0.15, g: 0.15, b: 0.15 });
    if (pickupAddrLines[1]) {
        drawText(pickupAddrLines[1], 56, routeY + routeH - 72, { font: 'bold', size: 9.5, r: 0.15, g: 0.15, b: 0.15 });
    }
    drawLine(56, routeY + 46, 40 + colW - 16, routeY + 46, 0.9, 0.9, 0.9, 0.5);
    drawText('Contact Person:', 56, routeY + 30, { font: 'normal', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.pickupContactName || '—', 56, routeY + 16, { font: 'bold', size: 9.5, r: 0.2, g: 0.2, b: 0.2 });
    drawText('Contact Phone:', 180, routeY + 30, { font: 'normal', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.pickupContactPhone || '—', 180, routeY + 16, { font: 'bold', size: 9.5, r: 0.2, g: 0.2, b: 0.2 });
    // Delivery Destination
    var delX = 40 + colW + 14;
    drawCard(delX, routeY, colW, routeH, '3. Delivery Destination');
    drawText('Delivery Address:', delX + 16, routeY + routeH - 45, { font: 'bold', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    var delAddr = data.deliveryAddress || '—';
    var delAddrLines = delAddr.length > 42 ? [delAddr.substring(0, 42), delAddr.substring(42, 84)] : [delAddr];
    drawText(delAddrLines[0], delX + 16, routeY + routeH - 60, { font: 'bold', size: 9.5, r: 0.15, g: 0.15, b: 0.15 });
    if (delAddrLines[1]) {
        drawText(delAddrLines[1], delX + 16, routeY + routeH - 72, { font: 'bold', size: 9.5, r: 0.15, g: 0.15, b: 0.15 });
    }
    drawLine(delX + 16, routeY + 46, delX + colW - 16, routeY + 46, 0.9, 0.9, 0.9, 0.5);
    drawText('Receiver Name:', delX + 16, routeY + 30, { font: 'normal', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.receiverName || '—', delX + 16, routeY + 16, { font: 'bold', size: 9.5, r: 0.2, g: 0.2, b: 0.2 });
    drawText('Receiver Phone:', delX + 140, routeY + 30, { font: 'normal', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.receiverPhoneNumber || '—', delX + 140, routeY + 16, { font: 'bold', size: 9.5, r: 0.2, g: 0.2, b: 0.2 });
    // Section 4 & 5: Cargo Specifications & Handling
    var cargoY = height - 510;
    var cargoH = 130;
    drawCard(40, cargoY, width - 80, cargoH, '4. Shipment Cargo & Handling Preferences');
    drawText('Items Being Transported:', 56, cargoY + cargoH - 46, { font: 'bold', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.whatAreYouSending || 'Standard freight shipment', 56, cargoY + cargoH - 62, { font: 'bold', size: 10, r: 0.1, g: 0.1, b: 0.1 });
    drawText('Preferred Pickup Date:', 56, cargoY + 40, { font: 'normal', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.preferredPickupDate || 'Earliest available', 56, cargoY + 24, { font: 'bold', size: 9.5, r: 0.15, g: 0.15, b: 0.15 });
    drawText('Preferred Time Window:', 220, cargoY + 40, { font: 'normal', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    drawText(data.preferredPickupTime || 'Flexible / Any time', 220, cargoY + 24, { font: 'bold', size: 9.5, r: 0.15, g: 0.15, b: 0.15 });
    drawText('Special Handling Requirements:', 380, cargoY + 40, { font: 'normal', size: 8.5, r: 0.5, g: 0.5, b: 0.5 });
    var handling = Array.isArray(data.specialHandling) && data.specialHandling.length > 0 ? data.specialHandling.join(', ') : 'Standard Freight Handling';
    drawText(handling, 380, cargoY + 24, { font: 'bold', size: 9.5, r: 0.15, g: 0.15, b: 0.15 });
    if (data.specialDeliveryInstructions) {
        drawText("Special Instructions: ".concat(data.specialDeliveryInstructions.substring(0, 85)), 56, cargoY + 8, { font: 'normal', size: 8, r: 0.4, g: 0.4, b: 0.4 });
    }
    // Next Steps & Dispatch Notice Card
    var noticeY = height - 605;
    fillRect(40, noticeY, width - 80, 82, 0.98, 0.95, 0.92);
    strokeRect(40, noticeY, width - 80, 82, 0.96, 0.70, 0.55, 1);
    drawText('NEXT STEPS & DISPATCH NOTICE', 56, noticeY + 62, { font: 'bold', size: 9.5, r: 0.75, g: 0.25, b: 0.05 });
    drawText('Thank you for choosing Speedyssey Logistics. Your transportation request has been recorded in our dispatch system.', 56, noticeY + 46, { font: 'normal', size: 8.5, r: 0.3, g: 0.2, b: 0.15 });
    drawText('Our operations team is currently reviewing vehicle capacity and route optimization. A dedicated logistics coordinator', 56, noticeY + 34, { font: 'normal', size: 8.5, r: 0.3, g: 0.2, b: 0.15 });
    drawText('will contact you within 2 business hours regarding availability, driver dispatch, and customized rates.', 56, noticeY + 22, { font: 'normal', size: 8.5, r: 0.3, g: 0.2, b: 0.15 });
    drawText('For immediate inquiries or modifications, please email support@speedyssey.com with your reference ID.', 56, noticeY + 10, { font: 'normal', size: 8.5, r: 0.3, g: 0.2, b: 0.15 });
    // Footer & Disclaimer
    var footerY = 32;
    drawLine(40, footerY + 20, width - 40, footerY + 20, 0.85, 0.85, 0.85, 0.5);
    drawText('Speedyssey Inc. - Transportation & Logistics Solutions', 40, footerY + 6, { font: 'bold', size: 8, r: 0.45, g: 0.45, b: 0.45 });
    drawText('https://speedyssey.com | support@speedyssey.com', width / 2 - 80, footerY + 6, { font: 'normal', size: 8, r: 0.45, g: 0.45, b: 0.45 });
    drawText('Page 1 of 1', width - 80, footerY + 6, { font: 'normal', size: 8, r: 0.45, g: 0.45, b: 0.45 });
    // Compile PDF content stream
    var contentStream = ops.join('\n');
    var streamBytes = new TextEncoder().encode(contentStream);
    var streamLength = streamBytes.length;
    var objects = [];
    objects.push('1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj');
    objects.push('2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj');
    objects.push("3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ".concat(width, " ").concat(height, "] /Contents 4 0 R /Resources << /Font << /F1 5 0 R /F2 6 0 R >> /ProcSet [/PDF /Text /ImageB /ImageC /ImageI] >> >>\nendobj"));
    objects.push("4 0 obj\n<< /Length ".concat(streamLength, " >>\nstream\n").concat(contentStream, "\nendstream\nendobj"));
    objects.push('5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>\nendobj');
    objects.push('6 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>\nendobj');
    var body = '%PDF-1.4\n%\xE2\xE3\xCF\xD3\n';
    var offsets = [];
    var encoder = new TextEncoder();
    var currentOffset = encoder.encode(body).length;
    for (var i = 0; i < objects.length; i++) {
        offsets.push(currentOffset);
        var objStr = objects[i] + '\n';
        body += objStr;
        currentOffset += encoder.encode(objStr).length;
    }
    var xrefOffset = currentOffset;
    var xref = 'xref\n0 7\n0000000000 65535 f \n';
    for (var i = 0; i < offsets.length; i++) {
        var offsetStr = String(offsets[i]).padStart(10, '0');
        xref += "".concat(offsetStr, " 00000 n \n");
    }
    var trailer = "trailer\n<< /Size 7 /Root 1 0 R >>\nstartxref\n".concat(xrefOffset, "\n%%EOF\n");
    var fullPdfString = body + xref + trailer;
    return encoder.encode(fullPdfString);
}
/**
 * Returns a Blob containing the PDF summary
 */
export function generateOrderSummaryPdfBlob(data) {
    var pdfBytes = generateOrderSummaryPdf(data);
    return new Blob([pdfBytes], { type: 'application/pdf' });
}
/**
 * Returns a base64-encoded string of the generated PDF
 */
export function generateOrderSummaryPdfBase64(data) {
    var pdfBytes = generateOrderSummaryPdf(data);
    var binary = '';
    var len = pdfBytes.byteLength;
    for (var i = 0; i < len; i++) {
        binary += String.fromCharCode(pdfBytes[i]);
    }
    return btoa(binary);
}
/**
 * Triggers an immediate browser download of the PDF summary
 */
export function downloadOrderSummaryPdf(data, filename) {
    var blob = generateOrderSummaryPdfBlob(data);
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url;
    link.download = filename || "Speedyssey-Order-Summary-".concat(data.referenceNumber || 'request', ".pdf");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(function () { return URL.revokeObjectURL(url); }, 1500);
}
